import * as React from 'react';

import { PAYMENT_PROVIDERS } from '@medipass/utils/constants';
import sdk from '@medipass/web-sdk';

import { ApplePayAvailabilityStatus, ApplePayPaymentRequest } from './applePay';
interface ApplePayButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  amount?: string;
  onError?: (...args: Array<any>) => any;
  paymentRequest?: ApplePayPaymentRequest;
  status?: ApplePayAvailabilityStatus;
  onSuccess?: (...args: Array<any>) => any;
}

const APPLE_PAY_BUTTON_CLASS_NAME =
  'pointer-events-auto h-10 min-h-[3rem] min-w-full rounded border-0 bg-black bg-contain bg-center bg-no-repeat bg-origin-content px-6 py-3 shadow-none';

export default function ApplePayButton({
  onSuccess,
  onError,
  paymentRequest,
  status = 'ineligible',
  ...restButtonProps
}: ApplePayButtonProps) {
  const handleClick = React.useCallback(() => {
    if (!paymentRequest) return;

    // Using an older version of Apple Pay Session should update someday
    const session = new window.ApplePaySession(3, paymentRequest as ApplePayJS.ApplePayPaymentRequest);

    session.onvalidatemerchant = async function (event) {
      try {
        const merchantSession = await sdk.applePay.performMerchantValidation(
          {
            validationUrl: event.validationURL,
            displayName: 'Tyro Health',
          },
          {
            requestConfig: {
              tokenType: 'session',
            },
          },
        );
        session.completeMerchantValidation(merchantSession);
      } catch (error) {
        onError?.(error);
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
      }
    };

    session.onpaymentauthorized = function (event) {
      if (!event?.payment?.token) {
        onError?.('No payment token found');
        session.completePayment(window.ApplePaySession.STATUS_FAILURE);
        return;
      }

      onSuccess?.({
        nonce: JSON.stringify(event?.payment?.token.paymentData),
        paymentProvider: PAYMENT_PROVIDERS.APPLE_PAY,
      });
      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
    };

    session.begin();
  }, [onError, onSuccess, paymentRequest]);

  if (status === 'checking') {
    return (
      <button
        type="button"
        aria-label="Apple Pay"
        className={APPLE_PAY_BUTTON_CLASS_NAME}
        data-testid="apple-pay-button-shell"
        disabled
        style={{ backgroundImage: '-webkit-named-image(apple-pay-logo-white)' }}
        {...restButtonProps}
      />
    );
  }

  if (status !== 'eligible' || !paymentRequest) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label="Apple Pay"
      onClick={handleClick}
      className={APPLE_PAY_BUTTON_CLASS_NAME}
      style={{ backgroundImage: '-webkit-named-image(apple-pay-logo-white)' }}
      {...restButtonProps}
    />
  );
}
